import React, { FunctionComponent, ReactNode, useContext, useMemo } from 'react';
import DataRecordTable, { CellTypeEnum } from 'src/libraries/data-record-table';
import Translate from 'ui/atoms/translate';
import {
  AdminApi,
  AdminInvitationsListOrderingEnum,
  AdminInvitationsValuesRetrieveFieldEnum,
  InvestmentInvitation,
  Invitation,
} from 'api';
import Currency from 'ui/atoms/currency';
import { ProductInvitationsStatus } from 'apps/issuer/pages/product-details/invitations/shared/product-invitation-status';
import WithDataRecord from 'src/hoc/WithDataRecord';
import { PaginationProps } from 'ui/molecules/pagination';
import { I18NContext } from 'src/ui/i18n/provider';
import useApiCall from 'src/hooks/use-api-call';
import { useTranslateWithStringArgs } from 'src/ui/hooks/use-translate';

export interface ProductInvitationsTableProps {
  productId: string;
  title?: ReactNode;
  loading?: boolean;
  paginationProps?: PaginationProps;
  goToDetails: (invitationId: string) => void;
  defaultLimit?: number;
  setFilterPopupHeight?: (height: number) => void;
}

const ProductInvitationsTable: FunctionComponent<ProductInvitationsTableProps> = WithDataRecord<AdminApi, Invitation>(
  AdminApi,
)(
  ({ data, ordering, onOrderBy, title, loading, goToDetails, paginationProps, setFilterPopupHeight, productId }) => {
    const { activeLocale } = useContext(I18NContext);

    const { makeAuthenticatedApi } = useApiCall();
    const translate = useTranslateWithStringArgs();
    const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

    const invitationStatusFilterOptions = useMemo(() => {
      return ['true', 'false'].map((hasInvestment) => ({
        id: hasInvestment,
        name: translate(`investmentInvitation.status.${hasInvestment === 'true' ? 'committed' : 'new'}`),
      }));
    }, [translate]);

    return (
      <>
        <DataRecordTable
          data={data as InvestmentInvitation[]}
          ordering={ordering}
          onOrderBy={onOrderBy}
          title={title}
          loading={loading}
          paginationProps={paginationProps}
          onGoToDetails={goToDetails}
          setFilterPopupHeight={setFilterPopupHeight}
          cells={[
            {
              title: <Translate name={`issuerProductDetails.campaigns.table.reference`} />,
              render: (invitation) => invitation.name,
              orderingFieldName: AdminInvitationsListOrderingEnum.NAME_ASC,
              type: CellTypeEnum.text,
              filterName: 'name',
              searchFieldName: AdminInvitationsValuesRetrieveFieldEnum.NAME,
              getFilterData: (params: any) =>
                adminApi.adminInvitationsValuesRetrieve({
                  ...params,
                  invitationType: 'investment_invitation',
                  tokenId: productId,
                }),
            },
            {
              title: <Translate name={`issuerProductDetails.campaigns.table.distributionPlatform`} />,
              render: (invitation) => invitation.distributionPlatformDetails.displayName || '-',
              orderingFieldName: AdminInvitationsListOrderingEnum.DISTRIBUTION_PLATFORM_DISPLAY_NAME_ASC,
              type: CellTypeEnum.text,
              filterName: 'distributionPlatformDisplayName',
              searchFieldName: AdminInvitationsValuesRetrieveFieldEnum.DISTRIBUTION_PLATFORM_DISPLAY_NAME,
              getFilterData: (params: any) =>
                adminApi.adminInvitationsValuesRetrieve({
                  ...params,
                  invitationType: 'investment_invitation',
                  tokenId: productId,
                }),
            },
            {
              title: <Translate name={`issuerProductDetails.campaigns.table.allocation`} />,
              alignContent: 'right',
              render: (invitation) => invitation.maxNumberOfTokens,
              orderingFieldName: AdminInvitationsListOrderingEnum.MAX_NUMBER_OF_TOKENS_ASC,
              type: CellTypeEnum.number,
              filterName: 'maxNumberOfTokens',
            },
            {
              title: <Translate name={`issuerProductDetails.campaigns.table.signed`} />,
              alignContent: 'right',
              render: (invitation) => `${invitation.signedPercentage.toFixed()}%`,
              orderingFieldName: AdminInvitationsListOrderingEnum.SIGNED_PERCENTAGE_ASC,
              type: CellTypeEnum.percentage,
              filterName: 'signedPercentage',
            },
            {
              title: <Translate name={`issuerProductDetails.campaigns.table.pricePerUnit`} />,
              alignContent: 'right',
              render: (invitation) => <Currency decimals="*">{invitation.pricePerToken}</Currency>,
              orderingFieldName: AdminInvitationsListOrderingEnum.PRICE_PER_UNIT_ASC,
              type: CellTypeEnum.currency,
              filterName: 'pricePerUnit',
            },
            {
              title: <Translate name={`issuerProductDetails.campaigns.table.status`} />,
              render: (invitation) => <ProductInvitationsStatus invitation={invitation} />,
              width: activeLocale === 'de' ? 230 : 196,
              orderingFieldName: AdminInvitationsListOrderingEnum.INVESTMENT_ASC,
              filterName: 'hasInvestment',
              getFilterData: () => {
                return invitationStatusFilterOptions;
              },
            },
          ]}
        />
      </>
    );
  },
  (api, props, offset, limit, ordering) => {
    return api.adminInvitationsList({
      invitationType: 'investment_invitation',
      tokenId: props.productId,
      offset,
      limit,
      ordering: ordering as AdminInvitationsListOrderingEnum,
      ...props,
    });
  },
);

export default ProductInvitationsTable;
